import { Switch as S } from 'radix-ui';
import { type ReactNode, useId } from 'react';

import { cx } from '@/lib/cx';

interface InterruptorProps {
  ligado: boolean;
  aoMudar: (ligado: boolean) => void;
  /** Nome acessível curto ("Captura automática"); o estado é lido pelo próprio switch. */
  rotulo: string;
  /** Estado em texto ao lado ("Ligada"), para não depender só da posição da bolinha. */
  estado?: ReactNode;
  disabled?: boolean;
  /** Por que está desabilitado, ou o que acontece ao ligar. */
  descricao?: ReactNode;
}

/** Chave liga/desliga para uma configuração que vale na hora de salvar (Radix Switch). */
export function Interruptor({
  ligado,
  aoMudar,
  rotulo,
  estado,
  disabled,
  descricao,
}: InterruptorProps) {
  const id = useId();
  return (
    <div className="flex items-start gap-3">
      <S.Root
        id={id}
        checked={ligado}
        onCheckedChange={aoMudar}
        disabled={disabled ?? false}
        aria-label={rotulo}
        aria-describedby={descricao ? `${id}-descricao` : undefined}
        className={cx(
          'relative mt-0.5 inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-pilula border border-texto-fraco bg-borda-forte',
          'transition-colors duration-[var(--duracao-rapida)] data-[state=checked]:border-marca data-[state=checked]:bg-marca',
          'focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-marca-suave disabled:cursor-not-allowed disabled:opacity-50',
        )}
      >
        <S.Thumb className="block size-3.5 translate-x-0.5 rounded-pilula bg-texto-suave shadow-baixa data-[state=checked]:bg-marca-texto transition-transform duration-[var(--duracao-media)] ease-[var(--ease-saida)] data-[state=checked]:translate-x-4 motion-reduce:transition-none" />
      </S.Root>
      <div className="grid gap-0.5">
        <label htmlFor={id} className="font-medium leading-tight">
          {rotulo}
          {estado !== undefined && (
            <span className="ml-2 text-sm font-normal text-texto-suave">{estado}</span>
          )}
        </label>
        {descricao && (
          <span id={`${id}-descricao`} className="text-xs text-texto-fraco">
            {descricao}
          </span>
        )}
      </div>
    </div>
  );
}
